/**
 * Analysis Report Exporter Utility
 *
 * Converts AnalysisReportData into well-formatted markdown for clipboard export.
 * Follows the lines[] builder pattern from playbookExporter.ts.
 *
 * Feb 2026: Restructured to match redesigned Analysis Report tab order.
 * - Company intelligence header
 * - fullReport first (strategic analysis)
 * - BSC objectives
 * - MECE / Deep Mode
 * - Quick Insights
 * - Appendix: Methodology & Evidence (synthesis + attestation)
 */

import { AnalysisReportData } from '@models/index'
import { slugify } from './slugify'

/**
 * Build a well-formatted markdown string from AnalysisReportData.
 * Includes ALL sections that have data, regardless of UI collapse state.
 */
export function buildAnalysisReportMarkdown(
  reportData: AnalysisReportData,
  strategyMap?: { pillars?: any[] }
): string {
  const lines: string[] = []

  lines.push('# Causeway Strategic Analysis Report')
  lines.push('')

  // Company Intelligence Header
  if (reportData.companyIntelligence?.company_name) {
    const ci = reportData.companyIntelligence
    const parts = [ci.company_name]
    if (ci.industry_description) parts.push(ci.industry_description)
    if (ci.revenue_tier) parts.push(ci.revenue_tier)
    lines.push(`**${parts.join(' | ')}**`)
    lines.push('')
  }

  lines.push(`*Generated on ${new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  })}*`)
  lines.push('')
  lines.push('---')
  lines.push('')

  // Section 1: Strategic Analysis Report (fullReport - now FIRST)
  if (reportData.fullReport) {
    lines.push(stripImageMarkers(reportData.fullReport))
    lines.push('')
  }

  // Section 2: BSC Strategic Objectives
  if (reportData.strategicObjectives?.length) {
    lines.push('## BSC Strategic Objectives')
    lines.push('')
    for (const perspective of ['Financial', 'Customer', 'Internal Process', 'Learning & Growth']) {
      const objectives = reportData.strategicObjectives.filter(
        (o: any) => o.perspective === perspective
      )
      if (objectives.length === 0) continue

      lines.push(`### ${perspective}`)
      lines.push('')
      for (const obj of objectives) {
        lines.push(`**${obj.objective_id}: ${obj.strategic_intent}** [${obj.outcome_type}]`)
        if (obj.description) {
          lines.push(`  ${obj.description}`)
        }
        if (obj.initiative_count > 0) {
          lines.push(`  Supporting Initiatives: ${obj.initiative_count}`)
        }
        lines.push('')
      }
    }
  }

  // Section 3: MECE Scenario Analysis
  if (reportData.meceAnalysis) {
    lines.push('## MECE Scenario Analysis')
    lines.push('')
    lines.push(stripImageMarkers(reportData.meceAnalysis))
    lines.push('')
  }

  // Section 4: Deep Mode Investigation Plan
  if (reportData.deepModePlan && reportData.planGenerated) {
    lines.push('## Deep Mode Investigation Plan')
    lines.push('')
    lines.push(`- **Query:** ${reportData.deepModePlan.user_query}`)
    lines.push(`- **Anchor Node:** ${reportData.deepModePlan.anchor_node}`)
    lines.push(`- **Hypotheses:** ${reportData.deepModePlan.hypotheses?.length || 0}`)

    if (reportData.deepModeReport?.metadata) {
      lines.push(`- **Nodes Assessed:** ${reportData.deepModeReport.metadata.nodes_assessed}`)
      lines.push(`- **High-Relevance Factors:** ${reportData.deepModeReport.metadata.high_relevance_nodes}`)
      lines.push(`- **Execution Time:** ${Math.round(reportData.deepModeReport.execution_time || 0)}s`)
    }
    lines.push('')

    if (reportData.deepModePlan.hypotheses?.length) {
      lines.push('### Hypotheses Evaluated')
      lines.push('')
      reportData.deepModePlan.hypotheses.forEach((h: any, idx: number) => {
        lines.push(`${idx + 1}. **${h.title}** — ${h.status} (${h.node_count || h.nodes?.length || 0} factors)`)
        lines.push(`   ${h.description}`)
        lines.push('')
      })
    }
  }

  // Section 5: Quick Insights
  if (reportData.quickResponses?.length) {
    lines.push('## Quick Insights')
    lines.push('')
    reportData.quickResponses.forEach((response, index) => {
      lines.push(`### Q${index + 1}: ${response.question}`)
      lines.push('')
      lines.push(stripImageMarkers(response.answer))
      lines.push('')
      lines.push('---')
      lines.push('')
    })
  }

  // Appendix: Methodology & Evidence
  const hasAppendix = reportData.strategicSynthesis || reportData.coverageAttestation
  if (hasAppendix) {
    lines.push('---')
    lines.push('')
    lines.push('## Appendix: Methodology & Evidence')
    lines.push('')

    // Strategic Synthesis
    if (reportData.strategicSynthesis) {
      lines.push('### Strategic Synthesis')
      lines.push('')

      if (reportData.strategicSynthesis.causal_narrative) {
        lines.push(reportData.strategicSynthesis.causal_narrative)
        lines.push('')
      }

      if (reportData.strategicSynthesis.pillar_derivation?.length) {
        lines.push('#### Evidence-Based Pillar Derivation')
        lines.push('')
        lines.push('| Pillar | Tactics | Confidence | Basis | Supporting Evidence |')
        lines.push('|--------|---------|------------|-------|---------------------|')
        for (const pillar of reportData.strategicSynthesis.pillar_derivation) {
          const evidence = pillar.supporting_evidence?.slice(0, 3)
            .map((n: any) => n.node_name)
            .join(', ') || 'Domain expertise'
          const basis = pillar.confidence_basis === 'graph_evidence' ? 'Graph Evidence' : 'Domain Expertise'
          lines.push(
            `| ${pillar.pillar_name} | ${pillar.tactic_count} | ${pillar.graph_confidence}% | ${basis} | ${evidence} |`
          )
        }
        lines.push('')
      }

      if (reportData.strategicSynthesis.methodology_summary) {
        const ms = reportData.strategicSynthesis.methodology_summary
        lines.push('#### Analysis Methodology')
        lines.push('')
        lines.push(`- **Questions Executed:** ${ms.questions_executed || 0}`)
        lines.push(`- **Nodes Explored:** ${ms.nodes_explored || 0}`)
        lines.push(`- **Nodes Approved:** ${ms.nodes_approved || 0}`)
        lines.push(`- **Overall Confidence:** ${ms.overall_confidence || 0}%`)
        lines.push('')
      }
    }

    // Coverage Attestation
    if (reportData.coverageAttestation) {
      lines.push('### Coverage Attestation')
      lines.push('')
      lines.push(stripImageMarkers(reportData.coverageAttestation))
      lines.push('')
    }
  }

  const __md = lines.join('\n')
  return __md.includes('```pillar-key-results')
    ? expandPillarKeyResults(__md, strategyMap)
    : __md
}

/**
 * Strip OMAP_IMAGE markers from text for clipboard export.
 * Images cannot be represented in plain markdown clipboard text.
 */
function stripImageMarkers(text: string): string {
  return text.replace(/<!--OMAP_IMAGE\|\|\|.+?\|\|\|.+?-->/g, '')
}

const PILLAR_KEY_RESULTS_FENCE = /```pillar-key-results\s*\n([\s\S]*?)\n```/g

function expandPillarKeyResults(md: string, strategyMap: { pillars?: any[] } | undefined): string {
  return md.replace(PILLAR_KEY_RESULTS_FENCE, (_match, pillarId: string) => {
    const id = pillarId.trim()
    const pillar = findPillarLite(strategyMap, id)
    if (!pillar) return ''
    const lines: string[] = []
    const seen = new Set<string>()
    let i = 1
    for (const tactic of pillar.tactics ?? []) {
      for (const m of tactic.metrics ?? []) {
        const key = m.id || m.name
        if (!key || seen.has(key)) continue
        seen.add(key)
        const value = m.target?.value ?? ''
        const unit = m.target?.unit
        const displayed =
          !value
            ? ''
            : (!unit || /\d[%$]/.test(value) || value.toLowerCase().includes(unit.toLowerCase()))
              ? value
              : `${value} ${unit}`
        lines.push(`${i}. **${m.name}**: ${displayed}`)
        i++
      }
    }
    return lines.length ? lines.join('\n') : ''
  })
}

function findPillarLite(strategyMap: { pillars?: any[] } | undefined, pillarId: string) {
  if (!pillarId) return undefined
  const pillars = strategyMap?.pillars ?? []
  const byId = pillars.find((p: any) => p.id === pillarId)
  if (byId) return byId
  const slugTarget = slugify(pillarId)
  return pillars.find((p: any) => p.name && slugify(p.name) === slugTarget)
}
